import { useState } from "react";
import type { Customer360Snapshot } from "../../../types/crm";
import { EditContractModal } from "../EditContractModal";
import { useLatestInvoices } from "../../../helpers/hooks/use-latest-invoices";
import {
  useChargeHistory,
  type CommissionChargeRow,
} from "../../../helpers/hooks/use-charge-history";

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function formatRate(rate: number | null): string {
  if (rate === null) return "—";
  return `${Math.round(rate * 100 * 100) / 100}%`;
}

/** Mirrors BILLING_TYPE_LABELS in pages/billing/types.ts. */
const BILLING_TYPE_LABELS: Record<string, string> = {
  commission: "Commission",
  fixed: "Fixed (platform fee)",
  hybrid: "Hybrid (platform fee + commission)",
  commission_or_flat: "Commission or flat fee",
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function BillingStatusBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-content-tertiary text-sm">—</span>;
  const lower = status.toLowerCase();
  let classes = "bg-surface-secondary text-content-secondary border-border-primary";
  if (lower === "active") classes = "bg-green-100 text-green-700 border-green-200";
  else if (lower === "past_due" || lower === "unpaid")
    classes = "bg-red-100 text-red-700 border-red-200";
  else if (lower === "trialing") classes = "bg-blue-100 text-blue-700 border-blue-200";
  else if (lower === "canceled" || lower === "cancelled")
    classes = "bg-gray-100 text-gray-600 border-gray-200";
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${classes}`}
    >
      {status}
    </span>
  );
}

function FieldRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <dt className="text-xs text-content-tertiary mb-0.5">{label}</dt>
      <dd className="text-sm text-content-primary">{children}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

interface Props {
  customerId: string;
  contract: Customer360Snapshot["contract"];
}

export function ContractBillingSection({ customerId, contract }: Props) {
  const [showEdit, setShowEdit] = useState(false);

  const isEmpty =
    contract.signedDate === null &&
    contract.commissionRate === null &&
    contract.planName === null &&
    contract.fixedFeeMonthly === null &&
    contract.billingType === null;

  return (
    <>
      <section className="bg-surface-primary rounded-xl border border-border-secondary shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-content-primary">
            Contract &amp; Billing
          </h2>
          <button
            onClick={() => setShowEdit(true)}
            className="px-3 py-1.5 text-xs font-medium text-content-secondary bg-surface-secondary border border-border-secondary rounded-md hover:bg-surface-hover transition-colors"
          >
            Edit Contract
          </button>
        </div>

        {isEmpty ? (
          <p className="text-sm text-content-tertiary">
            No contract on file — click <strong>Edit Contract</strong> to add one.
          </p>
        ) : (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
            <FieldRow label="Plan">
              {contract.planName ? (
                <span className="text-sm text-content-primary">{contract.planName}</span>
              ) : (
                <span className="text-content-tertiary">—</span>
              )}
            </FieldRow>

            <FieldRow label="Fixed fee (monthly)">
              {contract.fixedFeeMonthly !== null ? (
                <span className="text-sm text-content-primary">
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(contract.fixedFeeMonthly)}
                </span>
              ) : (
                <span className="text-content-tertiary">—</span>
              )}
            </FieldRow>

            <FieldRow label="Billing structure">
              {contract.billingType ? (
                <span className="text-sm text-content-primary">
                  {BILLING_TYPE_LABELS[contract.billingType]}
                </span>
              ) : (
                <span className="text-content-tertiary">—</span>
              )}
            </FieldRow>

            <FieldRow label="Commission rate">
              {formatRate(contract.commissionRate)}
            </FieldRow>

            {contract.billingType === "commission_or_flat" && (
              <FieldRow label="Minimum monthly fee">
                {contract.minimumFeeAmount !== null ? (
                  <span className="text-sm text-content-primary">
                    {new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(contract.minimumFeeAmount)}
                    {/* The floor is contractual but not yet enforced by the
                        charge path — say so, or a CSM will quote it as billed. */}
                    <span className="ml-2 text-xs text-amber-700">
                      not charged yet
                    </span>
                  </span>
                ) : (
                  <span className="text-content-tertiary">—</span>
                )}
              </FieldRow>
            )}

            <FieldRow label="Billing status">
              <BillingStatusBadge status={contract.billingStatus} />
            </FieldRow>

            <FieldRow label="Signed date">
              {formatDate(contract.signedDate)}
            </FieldRow>

            {contract.planDescription && (
              <div className="sm:col-span-2">
                <dt className="text-xs text-content-tertiary mb-0.5">
                  Plan description
                </dt>
                <dd className="text-sm text-content-primary whitespace-pre-wrap">
                  {contract.planDescription}
                </dd>
              </div>
            )}

            {contract.cancellationClause && (
              <div className="sm:col-span-2">
                <dt className="text-xs text-content-tertiary mb-0.5">
                  Cancellation clause
                </dt>
                <dd className="text-sm text-content-primary whitespace-pre-wrap">
                  {contract.cancellationClause}
                </dd>
              </div>
            )}

          </dl>
        )}

        {/* Latest invoices from Stripe — live fetch via
            /admin/customers/:id/invoices/latest. For all-in-one customers
            typically shows one subscription_cycle invoice (fixed fee) + one
            manual invoice (commission). */}
        <LatestInvoicesBlock customerId={customerId} />

        {/* Includes failed charges, which the Stripe-invoice block above omits. */}
        <ChargeHistoryBlock customerId={customerId} />
      </section>

      {showEdit && (
        <EditContractModal
          customerId={customerId}
          contract={contract}
          onClose={() => setShowEdit(false)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Latest invoices sub-section (Phase 2 Stage 7)
// ---------------------------------------------------------------------------

function LatestInvoicesBlock({ customerId }: { customerId: string }) {
  const { data, isLoading, isError } = useLatestInvoices(customerId, 5);

  return (
    <div className="mt-6 pt-6 border-t border-border-secondary">
      <h3 className="text-sm font-semibold text-content-primary mb-3">
        Latest invoices (Stripe)
      </h3>
      {isLoading && (
        <div className="text-xs text-content-tertiary">Loading...</div>
      )}
      {isError && (
        <div className="text-xs text-red-600">
          Failed to load invoices — check customer's Stripe link.
        </div>
      )}
      {data && data.length === 0 && (
        <div className="text-xs text-content-tertiary italic">
          No invoices on file.
        </div>
      )}
      {data && data.length > 0 && (
        <ul className="space-y-2">
          {data.map((inv) => (
            <li
              key={inv.id}
              className="flex items-center justify-between gap-4 px-3 py-2 bg-surface-secondary rounded border border-border-primary"
            >
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-medium text-content-primary">
                  {inv.number ?? inv.id}
                </span>
                <span className="text-xs text-content-tertiary">
                  {new Date(inv.createdAt).toLocaleDateString("en-US", {
                    timeZone: "America/New_York",
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                  {inv.billingReason && ` · ${inv.billingReason}`}
                </span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-sm font-medium text-content-primary">
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(inv.amountPaid || inv.amountDue)}
                </span>
                <InvoiceStatusBadge status={inv.status} />
                {inv.hostedUrl && (
                  <a
                    href={inv.hostedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline"
                  >
                    View
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const CHARGE_STATUS_STYLE: Record<string, string> = {
  paid: "bg-green-100 text-green-700",
  charged: "bg-blue-100 text-blue-700",
  pending: "bg-yellow-100 text-yellow-700",
  failed: "bg-red-100 text-red-700",
  cancelled: "bg-gray-100 text-gray-600",
};

function ChargeStatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${
        CHARGE_STATUS_STYLE[status] ?? "bg-gray-100 text-gray-700"
      }`}
    >
      {status}
    </span>
  );
}

function chargePeriod(charge: CommissionChargeRow): string {
  // "May 2026" — UTC-anchored so the viewer's timezone can't shift the month.
  return new Date(
    Date.UTC(charge.billingYear, charge.billingMonth - 1),
  ).toLocaleDateString("en-US", {
    timeZone: "UTC",
    year: "numeric",
    month: "short",
  });
}

function ChargeHistoryBlock({ customerId }: { customerId: string }) {
  const { data, isLoading, isError } = useChargeHistory(customerId);

  return (
    <div className="mt-6 pt-6 border-t border-border-secondary">
      <h3 className="text-sm font-semibold text-content-primary mb-3">
        Charge history
      </h3>
      {isLoading && (
        <div className="text-xs text-content-tertiary">Loading...</div>
      )}
      {isError && (
        <div className="text-xs text-red-600">Failed to load charge history.</div>
      )}
      {data && data.length === 0 && (
        <div className="text-xs text-content-tertiary italic">
          No charges on file.
        </div>
      )}
      {data && data.length > 0 && (
        <ul className="space-y-2">
          {data.map((charge) => (
            <li
              key={charge.id}
              className="flex items-center justify-between gap-4 px-3 py-2 bg-surface-secondary rounded border border-border-primary"
            >
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-medium text-content-primary">
                  {chargePeriod(charge)}
                  {charge.rechargeCount > 0 && (
                    <span className="ml-2 text-xs text-content-tertiary">
                      · retried {charge.rechargeCount}×
                    </span>
                  )}
                </span>
                {charge.status === "failed" && charge.failureReason && (
                  <span className="text-xs text-red-600 truncate">
                    {charge.failureReason}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-sm font-medium text-content-primary">
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(charge.totalAmount ?? charge.commissionAmount)}
                </span>
                <ChargeStatusBadge status={charge.status} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function InvoiceStatusBadge({ status }: { status: string | null }) {
  if (!status) return null;
  const color =
    status === "paid"
      ? "bg-green-100 text-green-700"
      : status === "open"
        ? "bg-yellow-100 text-yellow-700"
        : status === "uncollectible"
          ? "bg-red-100 text-red-700"
          : "bg-gray-100 text-gray-700";
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${color}`}>
      {status}
    </span>
  );
}
